<template>
  <v-container
    fluid
    class="ac2-page">
    <v-card class="rounded-lg table-card">
      <v-data-table
        :headers="header"
        :items="items"
        :options.sync="options"
        :items-per-page="options.itemsPerPage"
        :server-items-length="total"
        :loading="loading"
        hide-default-footer
        class="w-full"
        @update:options="onOptionsUpdate"
      >
        <template v-slot:top>
          <div class="d-flex align-center flex-wrap px-4 pt-4 pb-2">
            <h6 class="text-h6 mb-0 mr-4">
              Warehouse Config
            </h6>
            <v-spacer />
            <v-select
              v-model="filterStatus"
              :items="statusFilterOptions"
              item-text="text"
              item-value="value"
              label="สถานะ"
              color="secondary"
              class="flex-grow-0 mr-3"
              style="max-width: 160px;"
              outlined
              dense
              hide-details>
            </v-select>
            <search-box
              v-model="inputSearch"
              class="flex-grow-0 mr-3"
              style="max-width: 240px;"
              placeholder="ค้นหา owner หรือ wh_id"
              @on-search="getData(true)" />
            <v-btn
              color="secondary"
              depressed
              @click="openCreate()">
              <v-icon left>
                mdi-plus
              </v-icon>
              เพิ่ม Mapping
            </v-btn>
          </div>
          <v-divider />
        </template>

        <template #[`item.warehouse`]="{ item }">
          <div v-if="item.warehouse">
            <div class="text-bold">
              {{ item.warehouse.name }}
            </div>
            <span class="grey--text text--darken-1">
              {{ item.warehouse.code }} · #{{ item.warehouse.id }}
            </span>
          </div>
          <span v-else>-</span>
        </template>

        <template #[`item.whId`]="{ item }">
          {{ item.whId || '-' }}
        </template>

        <template #[`item.store`]="{ item }">
          {{ item.store && item.store.name ? item.store.name.toUpperCase() : '-' }}
        </template>

        <template #[`item.status`]="{ item }">
          <v-chip
            small
            :color="getStatusStyle(item.status).color"
            text-color="white">
            {{ item.status }}
          </v-chip>
        </template>

        <template #[`item.updatedAt`]="{ item }">
          <span style="white-space: nowrap;">
            {{ item.updatedAt | dateTimeUserFormat() }}
          </span>
        </template>

        <template #[`item.option`]="{ item }">
          <v-btn
            link
            icon
            x-small
            title="แก้ไข"
            @click="editItem(item)">
            <v-icon>
              mdi-pencil-outline
            </v-icon>
          </v-btn>
        </template>
      </v-data-table>
      <v-divider />
      <div class="d-flex align-center flex-wrap px-4 py-3">
        <span class="text-caption grey--text">
          Total {{ total }} items
        </span>
        <v-spacer />
        <v-pagination
          v-model="options.page"
          :length="pageCount"
          :total-visible="5"
          color="secondary"
          @input="getData()" />
      </div>
    </v-card>

    <v-dialog
      v-model="showForm"
      persistent
      max-width="480px">
      <v-card>
        <v-card-title>
          <span class="text-h5">{{ isEdit ? 'แก้ไข Warehouse Mapping' : 'เพิ่ม Warehouse Mapping' }}</span>
        </v-card-title>
        <v-card-text>
          <v-container>
            <v-form
              ref="ac2-warehouse-form"
              @submit.prevent="onSubmit()">
              <v-row>
                <v-col cols="12">
                  <v-autocomplete
                    v-model="formData.warehouse"
                    :items="warehouses"
                    :disabled="isEdit"
                    :loading="loadingWarehouse"
                    :rules="[(v) => !!v || 'กรุณาเลือก warehouse']"
                    item-text="name"
                    item-value="id"
                    label="Warehouse"
                    color="secondary"
                    outlined
                    dense>
                    <template #item="{ item }">
                      <span>{{ item.name }} <span class="grey--text">({{ item.code }} · #{{ item.id }})</span></span>
                    </template>
                  </v-autocomplete>
                </v-col>
                <v-col cols="12">
                  <v-text-field
                    :value="formData.owner"
                    label="Owner"
                    readonly
                    filled
                    hint="กำหนดอัตโนมัติจาก store ของ warehouse"
                    persistent-hint
                    color="secondary"
                    outlined
                    dense />
                </v-col>
                <v-col cols="12">
                  <v-text-field
                    v-model.trim="formData.whId"
                    label="AC2 Warehouse"
                    readonly
                    filled
                    color="secondary"
                    outlined
                    dense
                    hide-details />
                </v-col>
                <v-col cols="12">
                  <v-select
                    v-model="formData.status"
                    :items="statusOptions"
                    item-text="text"
                    item-value="value"
                    label="สถานะ"
                    color="secondary"
                    outlined
                    dense
                    hide-details />
                </v-col>
              </v-row>
            </v-form>
          </v-container>
        </v-card-text>
        <v-card-actions>
          <v-spacer></v-spacer>
          <v-btn
            color="red darken-1"
            text
            :disabled="loading"
            @click="closeForm()">
            Close
          </v-btn>
          <v-btn
            color="green darken-1"
            text
            :loading="loading"
            @click="onSubmit()">
            Submit
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>
  </v-container>
</template>

<script>
import { mapActions } from 'vuex'
import SearchBox from '@/components/SearchBox.vue'
import Ac2WarehouseProvider from '@/resources/Ac2WarehouseProvider'
import WarehouseProvider from '@/resources/WarehouseProvider'

const Ac2WarehouseService = new Ac2WarehouseProvider()
const WarehouseService = new WarehouseProvider()

export default {
  components: {
    SearchBox
  },
  data () {
    return {
      inputSearch: '',
      filterStatus: null,
      loading: false,
      loadingWarehouse: false,
      showForm: false,
      isEdit: false,
      warehouses: [],
      statusOptions: [
        { text: 'Active', value: 'active', color: 'success' },
        { text: 'Inactive', value: 'inactive', color: 'grey' }
      ],
      formData: {
        id: null,
        warehouse: null,
        owner: '',
        whId: '001',
        status: 'active'
      },
      total: 0,
      sortSnapshot: null,
      options: {
        page: 1,
        itemsPerPage: 25,
        sortBy: ['createdAt'],
        sortDesc: [true]
      },
      header: [
        { text: 'Warehouse', value: 'warehouse', sortable: false },
        { text: 'Owner', value: 'owner' },
        { text: 'AC2 Warehouse', value: 'whId' },
        { text: 'Store', value: 'store', sortable: false },
        { text: 'Status', value: 'status' },
        { text: 'Last update', value: 'updatedAt' },
        {
          text: 'Options',
          value: 'option',
          align: 'center',
          sortable: false,
          width: '110px'
        }
      ],
      items: []
    }
  },
  computed: {
    statusFilterOptions () {
      return [{ text: 'All', value: null }, ...this.statusOptions]
    },
    pageCount () {
      return Math.ceil(this.total / this.options.itemsPerPage) || 1
    }
  },
  watch: {
    filterStatus () {
      this.getData(true)
    },
    'formData.warehouse': function watchWarehouse (id) {
      if (this.isEdit) return
      const selected = this.warehouses.find((w) => w.id === id)
      this.formData.owner = this.resolveOwner(selected)
    }
  },
  created () {
    this.getData(true)
  },
  methods: {
    ...mapActions({
      setModal: 'Components/setModal',
      setSnackbar: 'Components/setSnackbar'
    }),
    onOptionsUpdate () {
      // Paging is handled by the separate v-pagination, so only refetch
      // when the sort changes. Skip the initial mount emit (created() loads).
      const sortKey = `${this.options.sortBy.join(',')}|${this.options.sortDesc.join(',')}`
      if (this.sortSnapshot === null) {
        this.sortSnapshot = sortKey
        return
      }
      if (sortKey !== this.sortSnapshot) {
        this.sortSnapshot = sortKey
        this.getData(true)
      }
    },
    getStatusStyle (status) {
      const found = this.statusOptions.find((s) => s.value === status)
      return { color: found?.color || 'grey' }
    },
    resolveOwner (warehouse) {
      const storeName = warehouse?.store?.name || ''
      if (storeName === 'Matter Makers') return 'MM'
      if (storeName === 'gentlewoman') return 'GW'
      return ''
    },
    async getData (clear = false) {
      if (clear) {
        this.options.page = 1
      }

      try {
        this.loading = true
        const { data } = await Ac2WarehouseService.getAc2Warehouses({
          page: this.options.page,
          limit: this.options.itemsPerPage,
          sortBy: this.options.sortBy[0],
          sortOrder: this.options.sortDesc[0] ? 'desc' : 'asc',
          status: this.filterStatus,
          search: this.inputSearch || null
        })
        this.items = data.results
        this.total = data.total || data.totalDocs || 0
      } catch (error) {
        this.setSnackbar({
          value: true,
          message: `Error ${error.message}`,
          type: 'error'
        })
      } finally {
        this.loading = false
      }
    },
    async fetchWarehouses () {
      if (this.warehouses.length) return

      try {
        this.loadingWarehouse = true
        const { data } = await WarehouseService.getAllStoreWarehouses({ status: 'active' })
        this.warehouses = data
      } catch (error) {
        this.setSnackbar({
          value: true,
          message: `Error ${error.message}`,
          type: 'error'
        })
      } finally {
        this.loadingWarehouse = false
      }
    },
    openCreate () {
      this.isEdit = false
      this.formData = {
        id: null,
        warehouse: null,
        owner: '',
        whId: '001',
        status: 'active'
      }
      this.fetchWarehouses()
      this.showForm = true
    },
    async editItem (item) {
      this.isEdit = true
      this.formData = {
        id: item.id,
        warehouse: item.warehouse ? item.warehouse.id : null,
        owner: item.owner || '',
        whId: item.whId || '001',
        status: item.status
      }
      this.showForm = true
      await this.fetchWarehouses()
      if (item.warehouse) {
        const matched = this.warehouses.find((w) => w.id === item.warehouse.id)
        if (matched) this.formData.warehouse = matched.id
      }
    },
    closeForm () {
      this.showForm = false
      if (this.$refs['ac2-warehouse-form']) {
        this.$refs['ac2-warehouse-form'].resetValidation()
      }
    },
    async onSubmit () {
      const valid = this.$refs['ac2-warehouse-form'].validate()
      if (!valid) return

      try {
        this.loading = true

        if (this.isEdit) {
          await Ac2WarehouseService.updateAc2WarehouseById(this.formData.id, {
            owner: this.formData.owner,
            whId: this.formData.whId,
            status: this.formData.status
          })
        } else {
          await Ac2WarehouseService.createAc2Warehouse({
            warehouse: this.formData.warehouse,
            whId: this.formData.whId,
            status: this.formData.status
          })
        }

        this.setSnackbar({
          value: true,
          message: this.isEdit ? 'แก้ไข mapping สำเร็จ' : 'เพิ่ม mapping สำเร็จ',
          type: 'success'
        })

        this.closeForm()
        this.getData(true)
      } catch (error) {
        this.setSnackbar({
          value: true,
          message: `Error ${error.message}`,
          type: 'error'
        })
      } finally {
        this.loading = false
      }
    }
  }
}
</script>
<style scoped>
.table-card {
  min-height: calc(100vh - 96px);
}
</style>
